// PrincipalView.js - Principal View (Circulars are read-only, plus private
// two-way chats with Admin, Boys Warden and Girls Warden) — sidebar layout
// COLORS: #0D47A1, #2196F3, #90CAF9, #E3F2FD ONLY
import { useState, useEffect, useRef } from "react";
import { useSchoolName } from "./schoolNameStore";
import { Hand, Circle, MessageCircle, Mail, ArrowLeft, GraduationCap, UserCog, Mars, Venus } from "lucide-react";
function StatusBadge({
  status
}) {
  const map = {
    sent: {
      bg: "#E3F2FD",
      color: "#0D47A1",
      border: "#90CAF9"
    },
    principal: {
      bg: "#E3F2FD",
      color: "#0D47A1",
      border: "#90CAF9"
    }
  };
  const labels = {
    sent: "Sent",
    principal: "Principal"
  };
  const cfg = map[status] || map.sent;
  return <span style={{
    background: cfg.bg,
    color: cfg.color,
    border: `1px solid ${cfg.border}`,
    padding: "4px 12px",
    borderRadius: 24,
    fontSize: 11,
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    gap: 6
  }}>
      <span style={{
      width: 6,
      height: 6,
      borderRadius: "50%",
      background: cfg.color,
      display: "inline-block"
    }} />
      {labels[status] || status}
    </span>;
}

// ── Reusable chat panel: Principal <-> a specific hostel's Warden ─────────
// Backed by the same /api/hostel/:hostelType/messages endpoints used on
// GirlsHostelManagement.js / BoysHostelManagement.js's "Messages" tab, so
// this is the SAME conversation, scoped by hostelType ("Boys" or "Girls").
// The Warden's page only ever sends `from: "Warden"`; here the Principal
// only ever sends `from: "Principal"` — replies always land with the
// correct warden because hostelType keeps Boys and Girls fully separate.
function HostelWardenChat({
  apiBase,
  hostelType,
  wardenLabel
}) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const endRef = useRef(null);
  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 6000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostelType]);
  useEffect(() => {
    endRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages]);
  async function fetchMessages() {
    try {
      const res = await fetch(`${apiBase}/api/hostel/${hostelType}/messages`);
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      setMessages(data || []);
    } catch (err) {
      console.error(`Failed to load ${hostelType} warden chat`, err);
    } finally {
      setLoading(false);
    }
  }
  async function sendMessage() {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setError('');
    try {
      const res = await fetch(`${apiBase}/api/hostel/${hostelType}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          from: 'Principal',
          priority: 'Normal',
          body: text
        })
      });
      if (!res.ok) throw new Error('Send failed');
      setInput('');
      fetchMessages();
    } catch (err) {
      console.error(err);
      setError(" Failed to send message. Please try again.");
    }
    setSending(false);
  }
  return <div style={{
    background: "#FFFFFF",
    borderRadius: 24,
    border: "1px solid #90CAF9",
    overflow: "hidden",
    boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
    display: "flex",
    flexDirection: "column",
    height: "100%"
  }}>
      <div style={{
      padding: "20px 24px",
      borderBottom: "1px solid #90CAF9",
      background: "#E3F2FD"
    }}>
        <div style={{
        fontWeight: 700,
        fontSize: 16,
        color: "#0D47A1"
      }}>
          <i className="fas fa-comments" style={{
          marginRight: 8,
          color: "#0D47A1"
        }}></i>
          Chat with {wardenLabel}
        </div>
        <div style={{
        fontSize: 11,
        color: "#0D47A1",
        marginTop: 4
      }}>
          Private conversation with the {wardenLabel} — scoped to the {hostelType} Hostel only
        </div>
      </div>

      <div style={{
      padding: "20px 24px",
      flex: 1,
      overflowY: "auto",
      display: "flex",
      flexDirection: "column",
      gap: 10,
      background: "#FAFCFF"
    }}>
        {loading ? <div style={{
        textAlign: "center",
        padding: 24,
        color: "#0D47A1"
      }}>
            <i className="fas fa-spinner fa-pulse" style={{
          marginRight: 8
        }}></i>
            Loading conversation...
          </div> : messages.length === 0 ? <div style={{
        textAlign: "center",
        padding: 24,
        color: "#0D47A1",
        fontSize: 13
      }}>
            No messages yet. Say hello to the {wardenLabel} below <Hand className="inline w-[1em] h-[1em] align-[-0.125em]" />
          </div> : messages.map(m => {
        const isPrincipal = m.from === 'Principal';
        return <div key={m._id} style={{
          alignSelf: isPrincipal ? "flex-end" : "flex-start",
          maxWidth: "75%",
          display: "flex",
          flexDirection: "column",
          alignItems: isPrincipal ? "flex-end" : "flex-start"
        }}>
                <div style={{
            background: isPrincipal ? "#0D47A1" : "#E3F2FD",
            color: isPrincipal ? "#FFFFFF" : "#0D47A1",
            padding: "10px 16px",
            borderRadius: isPrincipal ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
            fontSize: 14,
            lineHeight: 1.5,
            whiteSpace: "pre-wrap",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
          }}>
                  {m.priority === "Urgent" && <div style={{
              fontSize: 10.5,
              fontWeight: 800,
              letterSpacing: "0.4px",
              marginBottom: 4,
              color: isPrincipal ? "#FFE0B2" : "#C62828"
            }}>
                      <Circle className="inline w-[1em] h-[1em] align-[-0.125em]" /> URGENT
                    </div>}
                  {m.body}
                </div>
                <div style={{
            fontSize: 10,
            color: "#0D47A1",
            opacity: 0.7,
            marginTop: 4
          }}>
                  {isPrincipal ? "You" : wardenLabel} · {new Date(m.createdAt).toLocaleString('en-IN', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
            })}
                </div>
              </div>;
      })}
        <div ref={endRef} />
      </div>

      <div style={{
      padding: "16px 24px",
      borderTop: "1px solid #90CAF9",
      display: "flex",
      flexDirection: "column",
      gap: 8,
      background: "#FFFFFF"
    }}>
        {error && <div style={{
        color: "#C62828",
        fontSize: 12
      }}>{error}</div>}
        <div style={{
        display: "flex",
        gap: 10
      }}>
          <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter' && !sending) sendMessage();
        }} placeholder={`Type a message to the ${wardenLabel}...`} style={{
          flex: 1,
          padding: "10px 14px",
          borderRadius: 12,
          border: "1px solid #90CAF9",
          fontSize: 13,
          outline: "none",
          color: "#0D47A1"
        }} />
          <button onClick={sendMessage} disabled={!input.trim() || sending} style={{
          background: !input.trim() || sending ? "#90CAF9" : "linear-gradient(135deg, #0D47A1, #2196F3)",
          color: "#FFFFFF",
          border: "none",
          padding: "10px 22px",
          borderRadius: 12,
          fontSize: 13,
          fontWeight: 700,
          cursor: !input.trim() || sending ? "not-allowed" : "pointer",
          display: "flex",
          alignItems: "center",
          gap: 8
        }}>
            <i className="fas fa-paper-plane"></i>
            {sending ? "Sending..." : "Send"}
          </button>
        </div>
      </div>
    </div>;
}

// ── Chat panel: Principal <-> Admin (private, plus read-only circulars) ──
function AdminChatPanel({
  apiBase,
  circulars,
  circularsLoading,
  onRefreshCirculars
}) {
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(true);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [showCirculars, setShowCirculars] = useState(false);
  const chatEndRef = useRef(null);
  useEffect(() => {
    fetchChatMessages();
    const interval = setInterval(fetchChatMessages, 6000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!showCirculars) chatEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [chatMessages, showCirculars]);
  async function fetchChatMessages() {
    try {
      const res = await fetch(`${apiBase}/api/principal-messages`);
      const j = await res.json();
      if (j.success) {
        setChatMessages(j.messages);
        fetch(`${apiBase}/api/principal-messages/mark-read/principal`, {
          method: 'PUT'
        }).catch(() => {});
      }
    } catch (err) {
      console.error('Failed to load chat with admin', err);
    } finally {
      setChatLoading(false);
    }
  }
  async function sendChatMessage() {
    const text = chatInput.trim();
    if (!text || chatSending) return;
    setChatSending(true);
    try {
      const res = await fetch(`${apiBase}/api/principal-messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sender: 'principal',
          message: text
        })
      });
      const j = await res.json();
      if (j.success) {
        setChatInput('');
        fetchChatMessages();
      } else {
        alert('Failed to send message: ' + (j.error || 'Unknown error'));
      }
    } catch (err) {
      console.error(err);
      alert('Failed to send message: ' + err.message);
    }
    setChatSending(false);
  }
  const getRecipientText = r => {
    if (r.recipientsMeta?.toPrincipal) {
      const principalRecipient = r.recipients?.find(rec => rec.type === 'principal');
      if (principalRecipient?.name) return `${principalRecipient.name}`;
      return "Principal Only";
    }
    return "Unknown";
  };
  const getCircularStatus = r => r.recipientsMeta?.toPrincipal ? 'principal' : r.isDraft ? 'draft' : 'sent';
  return <div style={{
    background: "#FFFFFF",
    borderRadius: 24,
    border: "1px solid #90CAF9",
    overflow: "hidden",
    boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
    display: "flex",
    flexDirection: "column",
    height: "100%"
  }}>
      <div style={{
      padding: "20px 24px",
      borderBottom: "1px solid #90CAF9",
      background: "#E3F2FD",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      flexWrap: "wrap",
      gap: 10
    }}>
        <div>
          <div style={{
          fontWeight: 700,
          fontSize: 16,
          color: "#0D47A1"
        }}>
            <i className="fas fa-comments" style={{
            marginRight: 8,
            color: "#0D47A1"
          }}></i>
            Chat with Admin
          </div>
          <div style={{
          fontSize: 11,
          color: "#0D47A1",
          marginTop: 4
        }}>
            Private conversation — no one else can see this
          </div>
        </div>
        <div style={{
        display: "flex",
        gap: 8
      }}>
          <button onClick={() => setShowCirculars(false)} style={{
          background: !showCirculars ? "#0D47A1" : "#FFFFFF",
          color: !showCirculars ? "#FFFFFF" : "#0D47A1",
          border: "1px solid #90CAF9",
          padding: "6px 14px",
          borderRadius: 10,
          fontSize: 11,
          fontWeight: 700,
          cursor: "pointer"
        }}>
            <MessageCircle className="inline w-[1em] h-[1em] align-[-0.125em]" /> Chat
          </button>
          <button onClick={() => setShowCirculars(true)} style={{
          background: showCirculars ? "#0D47A1" : "#FFFFFF",
          color: showCirculars ? "#FFFFFF" : "#0D47A1",
          border: "1px solid #90CAF9",
          padding: "6px 14px",
          borderRadius: 10,
          fontSize: 11,
          fontWeight: 700,
          cursor: "pointer"
        }}>
            <Mail className="inline w-[1em] h-[1em] align-[-0.125em]" /> Messages ({circulars.length})
          </button>
        </div>
      </div>

      {showCirculars ? <div style={{
      padding: "20px 24px",
      flex: 1,
      overflowY: "auto",
      background: "#FAFCFF"
    }}>
          <div style={{
        display: "flex",
        justifyContent: "flex-end",
        marginBottom: 12
      }}>
            <button onClick={onRefreshCirculars} style={{
          background: "#E3F2FD",
          border: "1px solid #90CAF9",
          padding: "6px 14px",
          borderRadius: 10,
          fontSize: 11,
          fontWeight: 600,
          color: "#0D47A1",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 6
        }}>
              <i className="fas fa-sync-alt"></i> Refresh
            </button>
          </div>
          {circularsLoading ? <div style={{
        textAlign: "center",
        padding: 48,
        color: "#0D47A1"
      }}>
              <i className="fas fa-spinner fa-pulse" style={{
          fontSize: 36,
          display: "block",
          marginBottom: 12
        }}></i>
              <div style={{
          fontSize: 13
        }}>Loading messages...</div>
            </div> : circulars.length === 0 ? <div style={{
        textAlign: "center",
        padding: 60,
        color: "#0D47A1"
      }}>
              <div style={{
          fontSize: 56,
          marginBottom: 16
        }}><Mail className="inline w-[1em] h-[1em] align-[-0.125em]" /></div>
              <div style={{
          fontSize: 16,
          fontWeight: 600,
          color: "#0D47A1",
          marginBottom: 8
        }}>No Messages Yet</div>
              <div style={{
          fontSize: 13,
          color: "#0D47A1"
        }}>Admin will send you private messages here</div>
            </div> : <div style={{
        display: "flex",
        flexDirection: "column",
        gap: 16
      }}>
              {circulars.map(r => <div key={r._id} style={{
          border: "1px solid #90CAF9",
          borderRadius: 16,
          padding: 20,
          background: "#FFFFFF",
          position: "relative",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
        }}>
                  <div style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            background: "#0D47A1",
            borderRadius: "16px 0 0 16px"
          }} />
                  <div style={{
            paddingLeft: 16
          }}>
                    <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: 8
            }}>
                      <div style={{
                fontSize: 16,
                fontWeight: 700,
                color: "#0D47A1"
              }}>{r.title}</div>
                      <StatusBadge status={getCircularStatus(r)} />
                    </div>
                    <div style={{
              fontSize: 12,
              color: "#0D47A1",
              marginBottom: 8
            }}>
                      <i className="far fa-clock" style={{
                marginRight: 6
              }}></i>
                      {new Date(r.createdAt).toLocaleString('en-IN', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
                    </div>
                    <div style={{
              fontSize: 12,
              color: "#0D47A1",
              marginBottom: 12
            }}>
                      <i className="fas fa-user" style={{
                marginRight: 6,
                color: "#0D47A1"
              }}></i>
                      {getRecipientText(r)}
                    </div>
                    {r.message && <div style={{
              fontSize: 14,
              color: "#0D47A1",
              background: "#E3F2FD",
              padding: "16px 20px",
              borderRadius: 12,
              border: "1px solid #90CAF9",
              whiteSpace: "pre-wrap",
              lineHeight: 1.6
            }}>
                        {r.message}
                      </div>}
                    <div style={{
              marginTop: 12,
              fontSize: 11,
              color: "#0D47A1",
              textAlign: "right"
            }}>
                      <i className="fas fa-check-circle" style={{
                marginRight: 4,
                color: "#0D47A1"
              }}></i>
                      Received from Admin
                    </div>
                  </div>
                </div>)}
            </div>}
        </div> : <>
          <div style={{
        padding: "20px 24px",
        flex: 1,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        background: "#FAFCFF"
      }}>
            {chatLoading ? <div style={{
          textAlign: "center",
          padding: 24,
          color: "#0D47A1"
        }}>
                <i className="fas fa-spinner fa-pulse" style={{
            marginRight: 8
          }}></i>
                Loading conversation...
              </div> : chatMessages.length === 0 ? <div style={{
          textAlign: "center",
          padding: 24,
          color: "#0D47A1",
          fontSize: 13
        }}>
                No messages yet. Say hello to Admin below <Hand className="inline w-[1em] h-[1em] align-[-0.125em]" />
              </div> : chatMessages.map(m => {
          const isPrincipal = m.sender === 'principal';
          return <div key={m._id} style={{
            alignSelf: isPrincipal ? "flex-end" : "flex-start",
            maxWidth: "75%",
            display: "flex",
            flexDirection: "column",
            alignItems: isPrincipal ? "flex-end" : "flex-start"
          }}>
                    <div style={{
              background: isPrincipal ? "#0D47A1" : "#E3F2FD",
              color: isPrincipal ? "#FFFFFF" : "#0D47A1",
              padding: "10px 16px",
              borderRadius: isPrincipal ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
              fontSize: 14,
              lineHeight: 1.5,
              whiteSpace: "pre-wrap",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
            }}>
                      {m.message}
                    </div>
                    <div style={{
              fontSize: 10,
              color: "#0D47A1",
              opacity: 0.7,
              marginTop: 4
            }}>
                      {isPrincipal ? "You" : "Admin"} · {new Date(m.createdAt).toLocaleString('en-IN', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })}
                    </div>
                  </div>;
        })}
            <div ref={chatEndRef} />
          </div>

          <div style={{
        padding: "16px 24px",
        borderTop: "1px solid #90CAF9",
        display: "flex",
        gap: 10,
        background: "#FFFFFF"
      }}>
            <input value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter' && !chatSending) sendChatMessage();
        }} placeholder="Type a message to Admin..." style={{
          flex: 1,
          padding: "10px 14px",
          borderRadius: 12,
          border: "1px solid #90CAF9",
          fontSize: 13,
          outline: "none",
          color: "#0D47A1"
        }} />
            <button onClick={sendChatMessage} disabled={!chatInput.trim() || chatSending} style={{
          background: !chatInput.trim() || chatSending ? "#90CAF9" : "linear-gradient(135deg, #0D47A1, #2196F3)",
          color: "#FFFFFF",
          border: "none",
          padding: "10px 22px",
          borderRadius: 12,
          fontSize: 13,
          fontWeight: 700,
          cursor: !chatInput.trim() || chatSending ? "not-allowed" : "pointer",
          display: "flex",
          alignItems: "center",
          gap: 8
        }}>
              <i className="fas fa-paper-plane"></i>
              {chatSending ? "Sending..." : "Send"}
            </button>
          </div>
        </>}
    </div>;
}
export default function PrincipalView() {
  const API_BASE = process.env.REACT_APP_API_URL || 'https://schoolcrm-fzcv.onrender.com';
  const [circulars, setCirculars] = useState([]);
  const [circularsLoading, setCircularsLoading] = useState(true);
  const [principalName, setPrincipalName] = useState('');
  const schoolName = useSchoolName();
  const [activeContact, setActiveContact] = useState('admin'); // 'admin' | 'boys' | 'girls'

  useEffect(() => {
    const storedName = localStorage.getItem('principalName') || '';
    setPrincipalName(storedName);
    fetchPrincipalCirculars();
  }, []);
  async function fetchPrincipalCirculars() {
    setCircularsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/circulars`);
      const j = await res.json();
      if (j.success) {
        const principalCirculars = j.circulars.filter(c => c.recipientsMeta?.toPrincipal === true);
        setCirculars(principalCirculars);
      }
    } catch (err) {
      console.error(err);
    }
    setCircularsLoading(false);
  }
  const goToDashboard = () => {
    window.location.href = '/dashboard';
  };
  // Icons chosen to suit each role:
  // Admin      -> UserCog   (administrator / settings-person icon)
  // Boys Warden-> Mars      (male symbol, quick visual gender cue)
  // Girls Warden-> Venus    (female symbol, quick visual gender cue)
  const contacts = [{
    id: 'admin',
    label: 'Admin',
    sub: 'School Administrator',
    icon: <UserCog className="inline w-[1.1em] h-[1.1em] align-[-0.125em]" />
  }, {
    id: 'boys',
    label: 'Boys Warden',
    sub: 'Boys Hostel',
    icon: <Mars className="inline w-[1.1em] h-[1.1em] align-[-0.125em]" />
  }, {
    id: 'girls',
    label: 'Girls Warden',
    sub: 'Girls Hostel',
    icon: <Venus className="inline w-[1.1em] h-[1.1em] align-[-0.125em]" />
  }];
  return <div style={{
    minHeight: "100vh",
    background: "#E3F2FD",
    fontFamily: "'Inter', sans-serif",
    display: "flex"
  }}>
      {/* ══════════════════════ Sidebar ══════════════════════ */}
      <div style={{
      width: 300,
      minWidth: 300,
      background: "#FFFFFF",
      borderRight: "1px solid #90CAF9",
      display: "flex",
      flexDirection: "column",
      height: "100vh",
      position: "sticky",
      top: 0
    }}>
        {/* Back button */}
        <div style={{
        padding: "16px 16px 0 16px"
      }}>
          <button onClick={goToDashboard} style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          background: "#FFFFFF",
          border: "1px solid #90CAF9",
          padding: "10px 16px",
          borderRadius: 12,
          fontSize: 13,
          fontWeight: 600,
          color: "#0D47A1",
          cursor: "pointer",
          transition: "all 0.2s",
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)"
        }} onMouseEnter={e => {
          e.currentTarget.style.background = "#E3F2FD";
        }} onMouseLeave={e => {
          e.currentTarget.style.background = "#FFFFFF";
        }}>
            <span style={{
            fontSize: 16
          }}><ArrowLeft className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
            Back to Dashboard
          </button>
        </div>

        {/* School + Principal header */}
        <div style={{
        margin: 16,
        padding: "18px 18px",
        borderRadius: 20,
        background: "linear-gradient(135deg, #0D47A1, #2196F3, #90CAF9)",
        boxShadow: "0 4px 20px rgba(13,71,161,0.2)"
      }}>
          <div style={{
          fontSize: 11,
          color: "rgba(255,255,255,0.85)",
          letterSpacing: "0.6px",
          textTransform: "uppercase",
          fontWeight: 700,
          marginBottom: 4
        }}>
            <i className="fas fa-school" style={{
            marginRight: 6
          }}></i>
            {schoolName}
          </div>
          <div style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginTop: 8
        }}>
            <div style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: "rgba(255,255,255,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 22
          }}>
              <GraduationCap className="inline w-[1.2em] h-[1.2em] align-[-0.125em]" style={{
              color: "#FFFFFF"
            }} />
            </div>
            <div>
              <div style={{
              fontWeight: 800,
              fontSize: 15,
              color: "#FFFFFF",
              letterSpacing: "-0.2px"
            }}>
                {principalName || "Principal"}
              </div>
              <div style={{
              fontSize: 11,
              color: "rgba(255,255,255,0.8)",
              fontWeight: 500
            }}>
                Principal
              </div>
            </div>
          </div>
        </div>

        {/* Contacts list */}
        <div style={{
        padding: "0 16px",
        fontSize: 11,
        color: "#0D47A1",
        opacity: 0.7,
        fontWeight: 700,
        letterSpacing: "0.6px",
        textTransform: "uppercase",
        marginBottom: 8
      }}>
          Conversations
        </div>
        <div style={{
        padding: "0 12px",
        display: "flex",
        flexDirection: "column",
        gap: 6,
        overflowY: "auto",
        flex: 1
      }}>
          {contacts.map(c => {
          const isActive = activeContact === c.id;
          const badgeCount = c.id === 'admin' ? circulars.length : null;
          return <button key={c.id} onClick={() => setActiveContact(c.id)} style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            width: "100%",
            background: isActive ? "#E3F2FD" : "#FFFFFF",
            border: isActive ? "1px solid #0D47A1" : "1px solid transparent",
            padding: "12px 14px",
            borderRadius: 14,
            cursor: "pointer",
            textAlign: "left",
            transition: "all 0.15s"
          }} onMouseEnter={e => {
            if (!isActive) e.currentTarget.style.background = "#FAFCFF";
          }} onMouseLeave={e => {
            if (!isActive) e.currentTarget.style.background = "#FFFFFF";
          }}>
                <div style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: isActive ? "#0D47A1" : "#E3F2FD",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 18,
              flexShrink: 0,
              color: isActive ? "#FFFFFF" : "#0D47A1"
            }}>
                  {c.icon}
                </div>
                <div style={{
              flex: 1,
              minWidth: 0
            }}>
                  <div style={{
                fontSize: 13.5,
                fontWeight: 700,
                color: "#0D47A1"
              }}>{c.label}</div>
                  <div style={{
                fontSize: 11,
                color: "#0D47A1",
                opacity: 0.7,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}>
                    {c.sub}
                  </div>
                </div>
                {badgeCount != null && badgeCount > 0 && <span style={{
              background: "#0D47A1",
              color: "#FFFFFF",
              fontSize: 10,
              fontWeight: 700,
              borderRadius: 999,
              padding: "2px 8px",
              flexShrink: 0
            }}>
                    {badgeCount}
                  </span>}
              </button>;
        })}
        </div>
      </div>

      {/* ══════════════════════ Main conversation panel ══════════════════════ */}
      <div style={{
      flex: 1,
      padding: 24,
      height: "100vh",
      boxSizing: "border-box"
    }}>
        {activeContact === 'admin' && <AdminChatPanel apiBase={API_BASE} circulars={circulars} circularsLoading={circularsLoading} onRefreshCirculars={fetchPrincipalCirculars} />}
        {activeContact === 'boys' && <HostelWardenChat apiBase={API_BASE} hostelType="Boys" wardenLabel="Boys Warden" />}
        {activeContact === 'girls' && <HostelWardenChat apiBase={API_BASE} hostelType="Girls" wardenLabel="Girls Warden" />}
      </div>
    </div>;
}